import {
  BadgeCheck,
  BookOpenCheck,
  Building2,
  Calculator,
  FileCheck,
  FileSearch,
  FileText,
  FolderOpen,
  Globe2,
  HeartHandshake,
  Landmark,
  Languages,
  LockKeyhole,
  Plane,
  ShieldCheck,
  Stamp,
  Users,
} from "lucide-react";

export const CONTACT = {
  phone: "(206) 267-8801",
  phoneHref: "tel:+12062678801",
  email: "khalid@egaalservices.com",
  emailHref: "mailto:khalid@egaalservices.com",
  address: "21031 International Blvd Suite 12, SeaTac, WA 98198",
  shortAddress: "SeaTac, Washington",
  hours: [
    { days: "Monday, Tuesday, Thursday, Friday", time: "3:00 PM – 9:00 PM" },
    { days: "Saturday", time: "3:00 PM – 9:00 PM" },
    { days: "Wednesday & Sunday", time: "Closed" },
  ],
};

export const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Immigration Services", href: "/immigration-services" },
  { label: "About Us", href: "/about" },
  { label: "Additional Services", href: "/additional-services" },
  { label: "Contact", href: "/contact" },
];

export const IMAGES = {
  family: "https://images.unsplash.com/photo-1596510914965-9ae08acae566?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwZmFtaWx5JTIwaGFwcHklMjB0b2dldGhlcnxlbnwwfHx8fDE3ODY2MTEyODB8MA&ixlib=rb-4.1.0&q=85",
  reunion: "https://images.unsplash.com/photo-1611516818236-8faa056fb659?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwZmFtaWx5JTIwaGFwcHklMjB0b2dldGhlcnxlbnwwfHx8fDE3ODY2MTEyODB8MA&ixlib=rb-4.1.0&q=85",
  passport: "https://images.pexels.com/photos/7009478/pexels-photo-7009478.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  documents: "https://images.unsplash.com/photo-1603796846097-bee99e4a601f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMGRvY3VtZW50JTIwc2lnbmluZyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzgzMDc3ODM2fDA&ixlib=rb-4.1.0&q=85",
  consultation: "https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd",
};

export const IMMIGRATION_SERVICES = [
  { title: "Family Petitions", slug: "family-petitions", icon: Users, description: "Organized preparation for family-based petitions and supporting evidence." },
  { title: "Adjustment of Status", slug: "adjustment-of-status", icon: BadgeCheck, description: "Document preparation support for eligible applicants pursuing permanent residence." },
  { title: "Citizenship & Naturalization", slug: "citizenship-naturalization", icon: Landmark, description: "N-400 preparation, civics readiness guidance, and organized filing support." },
  { title: "Green Card Services", slug: "green-card-services", icon: BookOpenCheck, description: "Renewals, replacements, and supporting document organization." },
  { title: "Consular Processing", slug: "consular-processing", icon: Globe2, description: "Step-by-step support as cases move through embassies and consulates." },
  { title: "NVC Assistance", slug: "nvc-assistance", icon: Building2, description: "Help organizing civil documents, fees, forms, and case correspondence." },
  { title: "USCIS Applications", slug: "uscis-applications", icon: FileCheck, description: "Careful preparation and review of common USCIS application packets." },
  { title: "Travel Documents", slug: "travel-documents", icon: Plane, description: "Advance parole and travel document preparation support." },
  { title: "Affidavits & Support Forms", slug: "affidavits-support-forms", icon: FileText, description: "Affidavit preparation, supporting records, and clear document checklists." },
  { title: "FOIA Requests", slug: "foia-requests", icon: FileSearch, description: "Requests for immigration records and organized follow-up support." },
  { title: "Immigration Translations", slug: "immigration-translations", icon: Languages, description: "Certified translation coordination for immigration filings and records." },
  { title: "Case Document Organization", slug: "case-document-organization", icon: FolderOpen, description: "Document intake, checklists, copies, and administrative case support." },
];

export const PROCESS_STEPS = [
  { number: "01", title: "Private Consultation", description: "We listen first, understand your goals, and identify the right document preparation path." },
  { number: "02", title: "Personalized Checklist", description: "You receive a clear list of records, forms, translations, and supporting evidence." },
  { number: "03", title: "Careful Preparation", description: "We organize, prepare, and review your packet with confidentiality and attention to detail." },
  { number: "04", title: "Next-Step Support", description: "We help you understand notices, appointments, NVC updates, and follow-up requests." },
];

export const TESTIMONIALS = [
  {
    quote: "The process finally felt organized. Every document had a place, and every next step was explained clearly.",
    context: "Family petition client, Washington",
  },
  {
    quote: "Egaal helped our family prepare with patience and cultural understanding. We always knew what to bring and why.",
    context: "Naturalization support client",
  },
  {
    quote: "Professional, confidential, and thorough. The checklist made a stressful process much easier to manage.",
    context: "Consular processing client",
  },
];

export const FAQS = [
  {
    question: "Is Egaal a law firm?",
    answer: "No. Egaal provides immigration document preparation and administrative support services. We do not provide legal advice or legal representation, and we can help you understand when speaking with a licensed immigration attorney may be appropriate.",
  },
  {
    question: "Can you help clients outside Washington?",
    answer: "Yes. Egaal supports individuals and families throughout the United States with remote document preparation, translation coordination, and organized case support.",
  },
  {
    question: "What should I bring to my consultation?",
    answer: "Bring photo identification, immigration notices, prior applications, passports, vital records, and any correspondence from USCIS, NVC, or a U.S. embassy.",
  },
  {
    question: "Do you offer certified translations?",
    answer: "Yes. We coordinate certified translations for immigration, academic, personal, and official documents and help ensure translated records are presentation-ready.",
  },
  {
    question: "Are my documents confidential?",
    answer: "Confidentiality is central to our work. Your information is handled with discretion, care, and respect throughout the preparation process.",
  },
];

export const ADDITIONAL_SERVICES = [
  { title: "Certified Translations", slug: "certified-translations", icon: Languages, description: "Certified translation support for immigration, legal, academic, and personal records." },
  { title: "Notary Services", slug: "notary-services", icon: Stamp, description: "Convenient notarial support for affidavits, sworn statements, and important documents." },
  { title: "Tax Preparation", slug: "tax-preparation", icon: Calculator, description: "Professional tax preparation for individuals and families, including multilingual support." },
  { title: "Document Support", slug: "document-support", icon: ShieldCheck, description: "Copies, forms, correspondence, application organization, and administrative assistance." },
];

export const VALUES = [
  { title: "Trust Before Transactions", icon: HeartHandshake, description: "We build confidence through honest guidance, transparent next steps, and respectful communication." },
  { title: "Confidential by Design", icon: LockKeyhole, description: "Sensitive family records are handled with discretion from intake through final preparation." },
  { title: "Culturally Informed", icon: Globe2, description: "We serve immigrant families with patience, cultural awareness, and multilingual understanding." },
  { title: "Personally Guided", icon: Users, description: "Every family receives individual attention rather than a one-size-fits-all process." },
];
